import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FindBottleModal from "../components/FindBottleModal";
import { logout } from "../lib/auth";
import { useSessionStore } from "../state/sessionStore";
import type { SessionUser } from "../types/auth";

interface Props {
  user: SessionUser;
}

export default function PortalHub({ user }: Props) {
  const navigate = useNavigate();
  const { endRound } = useSessionStore();
  // null = closed; "search" opens the bottle finder on the text box, "scan" opens it on the camera.
  const [bottleFinder, setBottleFinder] = useState<null | "search" | "scan">(null);

  const roles = user?.bar_roles ?? [];

  const isOwner = roles.includes("bar_owner");
  const isManager = isOwner || roles.includes("bar_manager");
  const isHost = isManager || roles.includes("bar_host");
  const hasAdmin = isManager;
  const hasSecurity = isOwner;
  const isTablet = user.display_label?.toLowerCase().includes("tablet") || user.display_label?.toLowerCase().includes("kiosk");

  // --- ⚡ AUTO-BYPASS LOGIC ONLY FOR HARDWARE TABLETS ---
  useEffect(() => {
    if (!user) return;

    // Physical kiosk tablets bypass straight to their respective station
    if (isTablet) {
      if (roles.includes("bar_staff") || roles.includes("bar_bartender") || roles.includes("bar_lead")) {
        navigate("/station", { replace: true });
        return;
      }
      if (roles.includes("bar_host")) {
        navigate("/host", { replace: true });
        return;
      }
    }
  }, [user, isTablet, roles, navigate]);

  function handleSignOut() {
    endRound();
    logout();
    navigate("/login", { replace: true });
  }

  if (!user) return null;

  return (
    <div style={styles.shell}>
      {/* Top Navigation */}
      <nav style={styles.navbar}>
        <div style={styles.logo}>
          <img
            src="/img/sentinel-logo.svg"
            alt="The Sentinel Room"
            style={{ height: 28, width: "auto", display: "block" }}
          />
          <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: "1.5px", color: "var(--gold)", textTransform: "uppercase" }}>
            OS
          </span>
        </div>
        <div style={styles.navRight}>
          <button style={styles.profileBtn} onClick={handleSignOut}>
            Sign Out ({user.first_name || user.display_label?.split(" ")[0] || "User"})
          </button>
        </div>
      </nav>

      {bottleFinder && (
        <FindBottleModal
          onClose={() => setBottleFinder(null)}
          startScanning={bottleFinder === "scan"}
          backLabel="Done"
        />
      )}

      <main style={styles.main}>
        {/* Welcome Header */}
        <header style={styles.header}>
          <h1 style={styles.title}>
            Good evening, {user.first_name || user.display_label.split(" ")[0]}.
          </h1>
          {hasAdmin && (
            <p style={styles.subtitle}>Floor operations, back office management, and tablet kiosks.</p>
          )}
        </header>

        {/* Section: Floor Service & Shift Tools (Accessible to all Staff) */}
        <div style={styles.sectionHeader}>
          <span style={styles.sectionTitle}>Floor Service & Operations</span>
        </div>

        {/* Bottle quick search: type a name/UPC or scan a barcode. Reuses Station's finder. */}
        <div style={styles.quickSearch} role="search" aria-label="Find a bottle">
          <button
            type="button"
            style={styles.quickSearchField}
            onClick={() => setBottleFinder("search")}
            aria-label="Find a bottle by name or UPC"
          >
            <span aria-hidden="true">🔍</span>
            <span style={styles.quickSearchText}>Find Bottle</span>
          </button>
          <button
            type="button"
            style={styles.quickScanBtn}
            onClick={() => setBottleFinder("scan")}
            aria-label="Scan a bottle barcode"
            title="Scan barcode"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 7V5a2 2 0 012-2h2" /><path d="M17 3h2a2 2 0 012 2v2" />
              <path d="M21 17v2a2 2 0 01-2 2h-2" /><path d="M7 21H5a2 2 0 01-2-2v-2" />
              <line x1="7" y1="7" x2="7" y2="17" /><line x1="10" y1="7" x2="10" y2="17" />
              <line x1="13" y1="7" x2="13" y2="17" /><line x1="17" y1="7" x2="17" y2="17" />
            </svg>
            <span>Scan</span>
          </button>
        </div>

        <div style={styles.grid}>
          <button style={styles.card} onClick={() => navigate("/admin/shelf-map")}>
            <div style={styles.icon}>🗺️</div>
            <h2 style={styles.cardTitle}>Shelf Bottle Map</h2>
            <p style={styles.cardDesc}>
              2D visual grid & barcode scanner across Reserve, Library, Cabinet, Bar, and Lounge shelves.
            </p>
          </button>

          {!user?.is_tablet && (
            <button style={styles.card} onClick={() => navigate("/admin/cash-management")}>
              <div style={styles.icon}>💵</div>
              <h2 style={styles.cardTitle}>Cash Management</h2>
              <p style={styles.cardDesc}>
                Opening cash bank, mid-shift drops, end-of-night drawer reconciliation & deposit ledger.
              </p>
            </button>
          )}

          <button style={styles.card} onClick={() => navigate("/admin/operations")}>
            <div style={styles.icon}>📋</div>
            <h2 style={styles.cardTitle}>Shift Checklists</h2>
            <p style={styles.cardDesc}>
              Daily opening duties, shift handoff, floor sanitation, and closing task logs.
            </p>
          </button>

          <button style={styles.card} onClick={() => navigate("/admin/pick-list")}>
            <div style={styles.icon}>📦</div>
            <h2 style={styles.cardTitle}>Floor Pick List</h2>
            <p style={styles.cardDesc}>
              Bar stock replenishment, bottles needed from back stock, and low-inventory restock alerts.
            </p>
          </button>

          <button style={styles.card} onClick={() => navigate("/admin/events")}>
            <div style={styles.icon}>🎟️</div>
            <h2 style={styles.cardTitle}>Events & Tickets</h2>
            <p style={styles.cardDesc}>
              Upcoming tastings, master classes, ticket sales, attendee roster, and live guest check-in.
            </p>
          </button>

          {isHost && !isManager && (
            <button style={styles.card} onClick={() => navigate("/host")}>
              <div style={styles.icon}>📋</div>
              <h2 style={styles.cardTitle}>Host Stand</h2>
              <p style={styles.cardDesc}>
                Live guest arrivals, walk-in waiting list, table reservations, and intake management.
              </p>
            </button>
          )}

          {isOwner && (
            <>
              <button style={styles.card} onClick={() => navigate("/admin/recipes")}>
                <div style={styles.icon}>🍸</div>
                <h2 style={styles.cardTitle}>Cocktail Specs <span style={styles.ownerBadge}>OWNER ONLY</span></h2>
                <p style={styles.cardDesc}>
                  Official recipes, glassware, ice types, precise specs, and preparation notes.
                </p>
              </button>

              <button style={styles.card} onClick={() => navigate("/admin/prep-recipes")}>
                <div style={styles.icon}>🧪</div>
                <h2 style={styles.cardTitle}>Prep Recipes & Syrups <span style={styles.ownerBadge}>OWNER ONLY</span></h2>
                <p style={styles.cardDesc}>
                  Batching formulas, house syrups, infusions, garnishes, and cordial recipes.
                </p>
              </button>
            </>
          )}
        </div>

        {/* Section: Management & Back Office (Managers & Owners Only) */}
        {hasAdmin && (
          <>
            <div style={{ ...styles.sectionHeader, marginTop: 40 }}>
              <span style={styles.sectionTitle}>Management & Administration</span>
            </div>

            <div style={styles.grid}>
              <button
                style={{ ...styles.card, ...styles.span2 }}
                onClick={() => navigate("/admin")}
              >
                <div style={styles.icon}>⚙️</div>
                <h2 style={styles.cardTitle}>Back Office Hub</h2>
                <p style={styles.cardDesc}>
                  Full inventory ledger, purchase orders, spirit curation, CRM, communications, and settings.
                </p>
              </button>

              <button style={styles.card} onClick={() => navigate("/admin/activity")}>
                <div style={styles.icon}>📊</div>
                <h2 style={styles.cardTitle}>Dashboard & Reports</h2>
                <p style={styles.cardDesc}>
                  Audit activity log, comp/waste tracking, sales velocities, and operational metrics.
                </p>
              </button>

              {hasSecurity && (
                <button style={styles.card} onClick={() => navigate("/admin/staff-access")}>
                  <div style={styles.icon}>🔐</div>
                  <h2 style={styles.cardTitle}>
                    Staff & Security <span style={styles.ownerBadge}>OWNER ONLY</span>
                  </h2>
                  <p style={styles.cardDesc}>
                    Manage staff permissions, station tablet tokens, and security audits.
                  </p>
                </button>
              )}
            </div>

            {/* Section: Station Kiosks (Moved to Bottom) */}
            <div style={{ ...styles.sectionHeader, marginTop: 40 }}>
              <span style={styles.sectionTitle}>Station Kiosks (Bar & Host Stand)</span>
            </div>

            <div style={styles.grid}>
              <button style={styles.card} onClick={() => navigate("/station")}>
                <div style={styles.icon}>🍸</div>
                <h2 style={styles.cardTitle}>Bartender Station</h2>
                <p style={styles.cardDesc}>
                  Live cocktail scaling builder, active service trays, 86/available toggle, and build notes.
                </p>
              </button>

              <button style={styles.card} onClick={() => navigate("/host")}>
                <div style={styles.icon}>📋</div>
                <h2 style={styles.cardTitle}>Host Stand</h2>
                <p style={styles.cardDesc}>
                  Live guest check-in, walk-in waiting list, table reservations, and intake management.
                </p>
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  shell: {
    background: "var(--bg, #0C0907)",
    color: "var(--text, #F7F5F0)",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  navbar: {
    width: "100%",
    boxSizing: "border-box",
    padding: "16px 28px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid var(--border, #231D19)",
    background: "var(--bg, #0C0907)",
  },
  logo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  navRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  roleBadgeList: {
    display: "flex",
    gap: 6,
  },
  roleBadge: {
    fontSize: 11,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid rgba(249, 175, 54, 0.25)",
    padding: "3px 8px",
    borderRadius: 12,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  profileBtn: {
    background: "transparent",
    border: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: 12,
    cursor: "pointer",
    transition: "all 0.2s",
  },
  main: {
    width: "100%",
    maxWidth: 880,
    boxSizing: "border-box",
    padding: "40px 20px 60px",
  },
  header: {
    marginBottom: 28,
  },
  quickSearch: {
    display: "flex",
    gap: 10,
    marginBottom: 16,
  },
  quickSearchField: {
    flex: 1,
    minWidth: 0,
    minHeight: 52,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 16px",
    background: "var(--surface, #15110E)",
    border: "1px solid rgba(249, 175, 54, 0.35)",
    borderRadius: 12,
    color: "var(--muted, #A39D98)",
    fontSize: 16,
    textAlign: "left",
    cursor: "text",
  },
  quickSearchText: {
    flex: 1,
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  quickScanBtn: {
    minHeight: 52,
    minWidth: 52,
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "0 16px",
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid rgba(249, 175, 54, 0.35)",
    borderRadius: 12,
    color: "var(--gold, #F9AF36)",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontWeight: 400,
    fontSize: 28,
    margin: "0 0 6px 0",
    color: "var(--text, #F7F5F0)",
  },
  subtitle: {
    color: "var(--muted, #A39D98)",
    fontSize: 14,
    margin: 0,
  },
  sectionHeader: {
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 16,
    width: "100%",
  },
  span2: {
    gridColumn: "1 / -1",
  },
  card: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 14,
    padding: 24,
    color: "var(--text, #F7F5F0)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    textAlign: "left",
    cursor: "pointer",
    transition: "transform 0.15s ease, border-color 0.15s ease, background 0.15s ease",
    width: "100%",
    outline: "none",
    boxSizing: "border-box",
  },
  cardTitle: {
    margin: "0 0 8px 0",
    fontSize: 15,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    fontFamily: "inherit",
    fontWeight: 700,
    color: "var(--text, #F7F5F0)",
  },
  cardDesc: {
    margin: 0,
    color: "var(--muted, #A39D98)",
    fontSize: 13,
    lineHeight: "1.45",
  },
  icon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    background: "rgba(249, 175, 54, 0.1)",
    border: "1px solid rgba(249, 175, 54, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    marginBottom: 14,
  },
  ownerBadge: {
    display: "inline-block",
    fontSize: 9,
    background: "rgba(224, 80, 64, 0.15)",
    color: "var(--red, #FF8A7A)",
    padding: "2px 6px",
    borderRadius: "10px",
    marginLeft: 6,
    verticalAlign: "middle",
    letterSpacing: "0.5px",
    fontWeight: "bold",
  },
};

